import React, { useMemo } from "react";
import ReactPlayer from "react-player";
import { ChatSender, SystemMessages } from "../../common/constants";
import {
  VIDEO_PROVIDER_URLS,
  VIDEO_PROVIDER_MATCHERS,
  getUserConfigText,
  DEFAULT_TEXT,
} from "../../config";
import { useConfig } from "../Widget/ConfigContext";
import {
  useHistoryContext,
  allowedTags,
  replaceWithLink,
  getSourceFromNode,
  SystemMessage,
} from "./ChatHistory";
import { BubbleWrapper } from "./BubbleWrapper";

export function MessageBubble({ message, sender, minimal, toggleChatOpen }) {
  const { config } = useConfig();
  const { doSynth } = useHistoryContext();
  message = message.trim();

  const approvedVideoProviderUrls = useMemo(() => {
    let approvedUrls = [];
    Object.entries(config.videoProviders).forEach(([key, settingObj]) => {
      if (settingObj.enabled && key !== "facebook") {
        approvedUrls.push(VIDEO_PROVIDER_URLS[key]);
      }
    });
    return approvedUrls;
  }, [config]);

  const approvedVideoProviderMatchers = useMemo(() => {
    let matchers = [];
    Object.entries(config.videoProviders).forEach(([key, settingObj]) => {
      if (settingObj.enabled && VIDEO_PROVIDER_MATCHERS[key]) {
        matchers.push(VIDEO_PROVIDER_MATCHERS[key]);
      }
    });
    return matchers;
  }, [config]);

  function isValidImageUrl(testUrl) {
    if (config.approvedImageFormats.length < 1) {
      return false;
    } else {
      // if there are some approved image formats, validate the url. If valid,
      // check if it ends with one of the extensions. If it does, we can render
      // the image
      try {
        const url = new URL(testUrl);
        const valid = config.approvedImageFormats
          .split(",")
          .some((format) => url.toString().endsWith("." + format));
        return valid;
      } catch (_) {
        return false;
      }
    }
  }

  function isValidAudioUrl(testUrl) {
    if (config.approvedAudioFormats.length < 1) {
      return false;
    } else {
      // if there are some approved audio formats, validate the url. If valid,
      // check if it ends with one of the extensions. If it does, we can render
      // the file in a player
      try {
        const url = new URL(testUrl);
        const valid = config.approvedAudioFormats
          .split(",")
          .some((format) => url.toString().endsWith("." + format));
        return valid;
      } catch (_) {
        return false;
      }
    }
  }

  function isValidVideoUrl(testUrl) {
    if (
      config.approvedVideoFormats.length < 1 &&
      approvedVideoProviderUrls.length < 1 &&
      approvedVideoProviderMatchers.length < 1
    ) {
      return false;
    } else {
      // if there are some approved video formats, validate the url. If valid,
      // check if it ends with one of the extensions or starts with one of the
      // provider urls. If so, we can render the link in a player
      try {
        const url = new URL(testUrl);
        const valid =
          config.approvedVideoFormats
            .split(",")
            .some((format) => url.toString().endsWith("." + format)) ||
          approvedVideoProviderUrls.some((providerUrl) =>
            url.toString().startsWith(providerUrl),
          ) ||
          approvedVideoProviderMatchers.some(
            (matcher) => url.toString().match(matcher)[0] !== null,
          );
        return valid;
      } catch (_) {
        return false;
      }
    }
  }

  /**
   * Function to recursively sanitize the content
   *
   * @param {HTMLElement} node
   */
  function sanitizeNode(node) {
    // If the node is an element node
    if (node.nodeType === Node.ELEMENT_NODE) {
      // If the element's tag name is not allowed, remove it from the DOM
      if (!allowedTags.includes(node.tagName.toLowerCase())) {
        node.replaceWith(...node.childNodes); // Replace the element with its children
      } else {
        const tagName = node.tagName.toLowerCase();

        // if node is an image tag
        if (tagName === "img") {
          const src = node.getAttribute("src");

          // replace the image with a link if it's not an approved extension,
          // or removes it if the src is null
          if (src === null) {
            node.remove();
          } else if (!isValidImageUrl(src)) {
            replaceWithLink(node, src);
          }
        }

        // if node is an audio tag
        if (tagName === "audio") {
          const src = getSourceFromNode(node);

          // after we have the source, if it's null remove the node entirely.
          // If it's a string but not an acceptable url based on the config,
          // replace it with a link. Otherwise, remove it.
          if (src === null) {
            node.remove();
          } else if (!isValidAudioUrl(src)) {
            // replace the audio with a link if it's not an approved extension
            replaceWithLink(node, src);
          } else {
            // if the audio is an approved extension, create a uniform audio
            // element so it's easier to process
            const replacement = document.createElement("audio");
            replacement.setAttribute("src", src);
            node.replaceWith(replacement);
          }
        }

        // if node is a video tag
        if (tagName === "video") {
          const src = getSourceFromNode(node);

          // after we have the source, if it's null remove the node entirely.
          // If it's a string but not an acceptable url based on the config,
          // replace it with a link. Otherwise, remove it.
          if (src === null) {
            node.remove();
          } else if (!isValidVideoUrl(src)) {
            // replace the video with a link if it's not an approved extension
            replaceWithLink(node, src);
          } else {
            // if the audio is an approved extension, create a uniform audio
            // element so it's easier to process
            const replacement = document.createElement("video");
            replacement.setAttribute("src", src);
            node.replaceWith(replacement);
          }
        }

        // also check links to see if they match rich media settings. If yes,
        // replace them with the corresponding elements
        if (tagName === "a") {
          let src;

          if ((src = node.getAttribute("href"))) {
            // image
            if (isValidImageUrl(src)) {
              const replacement = document.createElement("img");
              replacement.setAttribute("src", src);
              node.replaceWith(replacement);
            } else if (isValidAudioUrl(src)) {
              // audio
              const replacement = document.createElement("audio");
              replacement.setAttribute("src", src);
              node.replaceWith(replacement);
            } else if (isValidVideoUrl(src)) {
              // video
              const replacement = document.createElement("video");
              replacement.setAttribute("src", src);
              node.replaceWith(replacement);
            } else {
              // if it's just a normal link, set to open in new tab and add
              // styling
              node.setAttribute("target", "_blank");
              node.classList.add("widget-link");
            }
          }
        }

        // If the element is allowed, recursively sanitize its children
        Array.from(node.childNodes).forEach(sanitizeNode);
      }
    }
  }

  function sanitizeHtml(inputHtml) {
    // Create a new DOMParser instance
    const parser = new DOMParser();

    // Parse the input HTML string into a document
    const doc = parser.parseFromString(inputHtml, "text/html");

    // Start the sanitization process from the body element of the document
    Array.from(doc.body.childNodes).forEach(sanitizeNode);

    // return the sanitized HTML as a tree
    return doc.body;
  }

  /**
   * @param {HTMLElement} sanitizedHtml
   */
  function createContents(sanitizedHtml) {
    // after html has been sanitized into a relatively flat tree of only
    // approved elements, render it as jsx elements. These will be wrapped in a
    // message bubble and presented as a single message
    const childNodes = Array.from(sanitizedHtml.childNodes);

    return childNodes.map((node, index) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent?.trim() === "") {
          return null;
        }

        return <span key={index}>{node.textContent}</span>;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toLowerCase();
        const src = node.getAttribute("src");

        switch (tag) {
          case "img":
            return (
              <img
                key={index}
                src={src}
                onClick={() => window.open(src, "_blank")}
                style={{
                  display: "block",
                  maxWidth: "100%",
                  height: "auto",
                  cursor: "pointer",
                }}
              ></img>
            );
          case "audio":
            return (
              <audio
                key={index}
                controls
                src={src}
                style={{
                  width: "2000px",
                  maxWidth: "100%",
                }}
              ></audio>
            );
          case "video":
            return (
              <ReactPlayer
                key={index}
                url={src}
                height={"auto"}
                width="20000px"
                controls
                style={{
                  height: "auto",
                  aspectRatio: "16 / 9",
                  maxWidth: "100%",
                }}
              ></ReactPlayer>
            );

          default:
            // for tags we're not handling specially, just render the contents
            // (for cases like text formatting)
            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: node.outerHTML }}
              ></span>
            );
        }
      }

      return null;
    });
  }

  function copyMessage() {
    navigator.clipboard.writeText(message);
  }

  function speakMessage(onEnd) {
    // convert message to text only by parsing it into a document and converting
    // non-text nodes to text
    const parser = new DOMParser();
    const doc = parser.parseFromString(message, "text/html");
    Array.from(doc.body.childNodes).forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE) {
        const tagName = node.tagName.toLowerCase();
        switch (tagName) {
          case "img":
            node.replaceWith("inline image");
            break;
          case "video":
            node.replaceWith("inline video player");
            break;
          case "audio":
            node.replaceWith("inline audio player");
            break;
          case "strong":
            node.replaceWith(...node.childNodes);
            break;
          case "a":
            const href = node.getAttribute("href");
            if (href) {
              const host = new URL(href);
              node.replaceWith(`link to ${host.hostname}`);
            } else {
              node.replaceWith("link");
            }
            break;
          default:
            if (
              Array.from(node.childNodes).some(
                (child) => child.nodeType === Node.TEXT_NODE,
              )
            ) {
              node.replaceWith(node.childNodes);
            }
        }
      }
    });

    doSynth(doc.body.innerHTML, onEnd);
  }

  // sanitized html and resulting display contents will not change unless the
  // message contents changes
  const messageContents = useMemo(() => {
    const sanitized = sanitizeHtml(message);
    return createContents(sanitized);
  }, [message]);

  // render system messages as inline text akin to disclaimers
  if (sender === ChatSender.system) {
    if (message in SystemMessages) {
      const text = getUserConfigText(
        config,
        SystemMessages[message], // map system message to text key
        DEFAULT_TEXT.en[SystemMessages[message]],
      );

      // if the user has text configured for the system message key, render it,
      // otherwise render nothing for this message
      if (text) {
        return <SystemMessage text={text} />;
      } else return;
    }
  } else {
    return (
      messageContents.length > 0 && (
        <>
          <BubbleWrapper
            minimal={minimal}
            sender={sender}
            copyMessage={copyMessage}
            speakMessage={speakMessage}
          >
            {messageContents}
          </BubbleWrapper>
        </>
      )
    );
  }
}
